import { useEffect, useMemo, useState } from "react";
import { api, API_BASE, formatDate, openDownload } from "@/lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import {
  Plus, Trash2, X, ChevronLeft, ChevronRight, CalendarDays, Check,
  Mail, Filter, Clock, AlertCircle, CheckCircle2, Grid3x3, LayoutGrid, Link2, Copy, ExternalLink,
  Paperclip, Upload, FileText as FileIcon, Image as ImageIcon, Download, GripVertical,
  Palette, StickyNote, Eye, Pencil, Printer,
} from "lucide-react";

const COLOR_PRESETS = [
  { hex: "", label: "Otomatik" },
  { hex: "#1E2A9C", label: "Lacivert" },
  { hex: "#0EA5E9", label: "Mavi" },
  { hex: "#10B981", label: "Yeşil" },
  { hex: "#F59E0B", label: "Amber" },
  { hex: "#EF4444", label: "Kırmızı" },
  { hex: "#EC4899", label: "Pembe" },
  { hex: "#8B5CF6", label: "Mor" },
  { hex: "#64748B", label: "Gri" },
];

const TYPE_LABEL = {
  meeting: "Toplantı",
  task: "Görev",
  reminder: "Hatırlatma",
  call: "Telefon",
  note: "Not",
  other: "Diğer",
};

const TYPE_COLOR = {
  meeting: "#3B82F6",
  task: "#8B5CF6",
  reminder: "#F59E0B",
  call: "#EC4899",
  note: "#64748B",
  other: "#0EA5E9",
};

const SOURCE_LABEL = {
  manual: "Manuel",
  proforma: "Proforma",
  cheque: "Çek",
  loan: "Kredi",
  recurring: "Tekrarlayan",
};

const SOURCE_COLOR = {
  manual: "#1E2A9C",
  proforma: "#0EA5E9",
  cheque: "#10B981",
  loan: "#F59E0B",
  recurring: "#8B5CF6",
};

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayISO = () => iso(new Date());

function monthMatrix(year, month) {
  // month: 0-indexed. Returns array of weeks; each week is 7 Date objects (Mon-Sun).
  const first = new Date(year, month, 1);
  const startDay = (first.getDay() + 6) % 7; // Monday=0
  const start = new Date(year, month, 1 - startDay);
  const weeks = [];
  const cur = new Date(start);
  for (let w = 0; w < 6; w++) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      week.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    weeks.push(week);
  }
  return weeks;
}

function weekDays(anchor) {
  // Returns Mon..Sun starting from Monday of the week containing anchor
  const d = new Date(anchor);
  const shift = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - shift);
  const days = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(d);
    day.setDate(d.getDate() + i);
    days.push(day);
  }
  return days;
}

export default function Agenda() {
  const [today] = useState(new Date());
  const [view, setView] = useState("month"); // 'month' | 'week'
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [weekAnchor, setWeekAnchor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [caris, setCaris] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null); // {mode:'new'|'edit', ev}
  const [selectedDate, setSelectedDate] = useState(todayISO());
  const [filters, setFilters] = useState({
    manual: true, proforma: true, cheque: true, loan: true, recurring: true, hideDone: false,
  });
  const [cariFilter, setCariFilter] = useState(""); // "" = all, "none" = only events without cari, else cari id
  const [icsOpen, setIcsOpen] = useState(false);
  const [icsUrl, setIcsUrl] = useState("");

  const [form, setForm] = useState({
    title: "", description: "", notes: "", date: todayISO(), time: "",
    event_type: "task", priority: "medium", remind_email: false, cari_id: "", color: "",
  });

  const weeks = useMemo(() => monthMatrix(cursor.getFullYear(), cursor.getMonth()), [cursor]);
  const monthStart = iso(weeks[0][0]);
  const monthEnd = iso(weeks[weeks.length - 1][6]);

  const weekCells = useMemo(() => weekDays(weekAnchor), [weekAnchor]);
  const weekStart = iso(weekCells[0]);
  const weekEnd = iso(weekCells[6]);

  const rangeStart = view === "month" ? monthStart : weekStart;
  const rangeEnd = view === "month" ? monthEnd : weekEnd;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agenda/events", { params: { start: rangeStart, end: rangeEnd } });
      setEvents(data.events);
    } catch {
      toast.error("Ajanda yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const loadCaris = async () => {
    try {
      const { data } = await api.get("/cari");
      setCaris(data);
    } catch (err) { console.error("Cari listesi yüklenemedi", err); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rangeStart, rangeEnd]);
  useEffect(() => { loadCaris(); }, []);

  const cariMap = useMemo(() => Object.fromEntries(caris.map(c => [c.id, c.name])), [caris]);

  const filteredEvents = useMemo(() => {
    return events.filter(e => filters[e.source] !== false)
                 .filter(e => !filters.hideDone || !e.done)
                 .filter(e => {
                   if (!cariFilter) return true;
                   if (cariFilter === "__none__") return !e.cari_id;
                   return e.cari_id === cariFilter;
                 });
  }, [events, filters, cariFilter]);

  const byDate = useMemo(() => {
    const map = {};
    filteredEvents.forEach(e => {
      map[e.date] = map[e.date] || [];
      map[e.date].push(e);
    });
    return map;
  }, [filteredEvents]);

  const dayEvents = byDate[selectedDate] || [];
  const upcomingEvents = filteredEvents
    .filter(e => e.date >= todayISO())
    .slice(0, 10);

  const openNew = (date, time) => {
    setForm({ title: "", description: "", notes: "", date: date || selectedDate,
              time: time || "", event_type: "task", priority: "medium", remind_email: false, cari_id: "", color: "" });
    setModal({ mode: "new" });
  };

  const openEdit = (ev) => {
    if (ev.source !== "manual") {
      toast.info("Otomatik etkinlikler kaynağından düzenlenmelidir");
      return;
    }
    setForm({
      title: ev.title, description: ev.description || "",
      notes: ev.notes || "",
      date: ev.date, time: ev.time || "",
      event_type: ev.event_type || "task",
      priority: ev.priority || "medium",
      remind_email: !!ev.remind_email,
      cari_id: ev.cari_id || "",
      color: ev.color || "",
    });
    setModal({ mode: "edit", ev });
  };

  const openIcs = async () => {
    try {
      const { data } = await api.get("/agenda/feed-token");
      const url = `${API_BASE}/api/agenda/calendar.ics?token=${encodeURIComponent(data.token)}`;
      setIcsUrl(url);
      setIcsOpen(true);
    } catch {
      toast.error("Feed URL alınamadı");
    }
  };

  const copyIcs = async () => {
    try {
      await navigator.clipboard.writeText(icsUrl);
      toast.success("URL kopyalandı");
    } catch {
      toast.error("Kopyalama başarısız");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (modal.mode === "new") {
        await api.post("/agenda/events", form);
        toast.success("Etkinlik eklendi");
      } else {
        await api.put(`/agenda/events/${modal.ev.id}`, form);
        toast.success("Etkinlik güncellendi");
      }
      setModal(null);
      load();
    } catch {
      toast.error("Kayıt başarısız");
    }
  };

  const del = async (ev) => {
    if (ev.source !== "manual") { toast.info("Otomatik etkinlik silinemez"); return; }
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/agenda/events/${ev.id}`);
    load();
  };

  const toggle = async (ev) => {
    if (ev.source !== "manual") { toast.info("Otomatik etkinlik işaretlenemez"); return; }
    await api.post(`/agenda/events/${ev.id}/toggle`);
    load();
  };

  const sendDaily = async () => {
    try {
      const { data } = await api.post("/agenda/send-daily");
      if (data.status === "skipped") toast.info(data.reason);
      else toast.success(`E-posta gönderildi (${data.count} etkinlik)`);
    } catch {
      toast.error("E-posta gönderilemedi");
    }
  };

  // ---- Attachments (upload/delete) — used from modal
  const uploadAttachment = async (eid, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post(`/agenda/events/${eid}/attachments`, fd,
      { headers: { "Content-Type": "multipart/form-data" } });
    toast.success(`Yüklendi: ${data.name}`);
    return data;
  };

  const deleteAttachment = async (eid, aid) => {
    await api.delete(`/agenda/events/${eid}/attachments/${aid}`);
    toast.success("Ek silindi");
  };

  // ---- Drag & drop (weekly view): move manual events
  const moveEvent = async (evId, newDate, newTime) => {
    try {
      const ev = events.find(e => e.id === evId);
      if (!ev || ev.source !== "manual") return;
      await api.put(`/agenda/events/${evId}`, { date: newDate, time: newTime || "" });
      toast.success(`Taşındı: ${newDate}${newTime ? ' ' + newTime : ''}`);
      load();
    } catch {
      toast.error("Taşıma başarısız");
    }
  };

  const goPrev = () => {
    if (view === "month") setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
    else { const d = new Date(weekAnchor); d.setDate(d.getDate() - 7); setWeekAnchor(d); }
  };
  const goNext = () => {
    if (view === "month") setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
    else { const d = new Date(weekAnchor); d.setDate(d.getDate() + 7); setWeekAnchor(d); }
  };
  const goToday = () => {
    const t = new Date();
    setCursor(new Date(t.getFullYear(), t.getMonth(), 1));
    setWeekAnchor(t);
    setSelectedDate(todayISO());
  };

  const monthLabel = cursor.toLocaleDateString("tr-TR", { year: "numeric", month: "long" });
  const weekLabel = `${weekCells[0].toLocaleDateString("tr-TR", { day: "numeric", month: "short" })} — ${weekCells[6].toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" })}`;
  const DOW = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
  const HOURS = Array.from({ length: 16 }, (_, i) => i + 7); // 07..22

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold">Kişisel</div>
          <h1 className="font-display text-4xl font-extrabold text-slate-900 dark:text-slate-100 mt-1">Ajanda</h1>
          <p className="text-sm text-slate-500 mt-1">Toplantı, görev, hatırlatma · Finansal olaylar otomatik eklenir</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="agenda-print" onClick={() => {
            const t = localStorage.getItem("madymark_token");
            const anchor = view === "week" ? weekStart : todayISO();
            openDownload(`/api/agenda/week.pdf`, { start: anchor });
          }}
            className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:border-amber-500 text-slate-700 dark:text-slate-200 hover:text-amber-600 font-bold transition">
            <Printer className="w-4 h-4" /> Haftayı Yazdır
          </button>
          <button data-testid="agenda-ics" onClick={openIcs}
            className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:border-emerald-500 text-slate-700 dark:text-slate-200 hover:text-emerald-600 font-bold transition">
            <Link2 className="w-4 h-4" /> Google Calendar'a Ekle
          </button>
          <button data-testid="agenda-send-daily" onClick={sendDaily}
            className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:border-[#1E2A9C] text-slate-700 dark:text-slate-200 hover:text-[#1E2A9C] font-bold transition">
            <Mail className="w-4 h-4" /> Günlük Özet Gönder
          </button>
          <button data-testid="add-event" onClick={() => openNew()}
            className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[#1E2A9C] text-white font-bold hover:bg-[#121A68] shadow-lg shadow-[#1E2A9C]/20">
            <Plus className="w-4 h-4" /> Yeni Etkinlik
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 p-4 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-2 text-xs uppercase font-bold text-slate-500 tracking-widest mr-2">
          <Filter className="w-3.5 h-3.5" /> Filtre
        </div>
        {Object.entries(SOURCE_LABEL).map(([k, l]) => (
          <button key={k} data-testid={`filter-${k}`}
            onClick={() => setFilters({ ...filters, [k]: !filters[k] })}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${filters[k]
              ? "text-white"
              : "bg-slate-100 dark:bg-slate-800 text-slate-500"}`}
            style={filters[k] ? { backgroundColor: SOURCE_COLOR[k] } : {}}>
            {l}
          </button>
        ))}
        <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1" />
        <select data-testid="filter-cari" value={cariFilter} onChange={(e) => setCariFilter(e.target.value)}
          className="px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-0 focus:ring-2 focus:ring-[#1E2A9C] cursor-pointer">
          <option value="">Tüm Cariler</option>
          <option value="__none__">— Cari yok —</option>
          {caris.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <div className="flex-1" />
        <div className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
          <button data-testid="view-month" onClick={() => setView("month")}
            className={`flex items-center gap-1 px-3 py-1 rounded text-xs font-bold transition ${view === "month" ? "bg-white dark:bg-slate-900 text-[#1E2A9C] shadow-sm" : "text-slate-500"}`}>
            <LayoutGrid className="w-3.5 h-3.5" /> Aylık
          </button>
          <button data-testid="view-week" onClick={() => setView("week")}
            className={`flex items-center gap-1 px-3 py-1 rounded text-xs font-bold transition ${view === "week" ? "bg-white dark:bg-slate-900 text-[#1E2A9C] shadow-sm" : "text-slate-500"}`}>
            <Grid3x3 className="w-3.5 h-3.5" /> Haftalık
          </button>
        </div>
        <label className="flex items-center gap-2 text-xs font-bold text-slate-600 dark:text-slate-300 cursor-pointer">
          <input type="checkbox" data-testid="filter-hidedone" checked={filters.hideDone}
            onChange={(e) => setFilters({ ...filters, hideDone: e.target.checked })} />
          Tamamlananları gizle
        </label>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Calendar */}
        <div className="lg:col-span-2 p-5 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <button data-testid="cal-prev" onClick={goPrev} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><ChevronLeft className="w-5 h-5" /></button>
              <div className="font-display text-xl font-extrabold text-slate-900 dark:text-slate-100 capitalize min-w-[220px] text-center">{view === "month" ? monthLabel : weekLabel}</div>
              <button data-testid="cal-next" onClick={goNext} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><ChevronRight className="w-5 h-5" /></button>
            </div>
            <button data-testid="cal-today" onClick={goToday} className="px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200">Bugün</button>
          </div>

          {view === "month" && (
          <>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DOW.map(d => (
              <div key={d} className="text-[10px] uppercase tracking-widest text-slate-500 font-bold text-center py-1">{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {weeks.map((week, wi) => week.map((d, di) => {
              const dISO = iso(d);
              const inMonth = d.getMonth() === cursor.getMonth();
              const isToday = dISO === todayISO();
              const isSelected = dISO === selectedDate;
              const evs = byDate[dISO] || [];
              return (
                <button key={`${wi}-${di}`}
                  data-testid={`day-${dISO}`}
                  onClick={() => setSelectedDate(dISO)}
                  onDoubleClick={() => openNew(dISO)}
                  className={`min-h-[80px] p-1.5 rounded-lg border text-left transition group ${
                    isSelected ? "border-[#1E2A9C] bg-[#1E2A9C]/5" :
                    isToday ? "border-emerald-500" : "border-slate-100 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-600"
                  } ${!inMonth ? "opacity-40" : ""}`}>
                  <div className={`text-xs font-bold ${isToday ? "text-emerald-600" : "text-slate-700 dark:text-slate-300"}`}>
                    {d.getDate()}
                  </div>
                  <div className="space-y-0.5 mt-1">
                    {evs.slice(0, 3).map(e => (
                      <div key={e.id} className={`text-[10px] px-1.5 py-0.5 rounded truncate font-semibold ${e.done ? "line-through opacity-50" : ""}`}
                        style={{ backgroundColor: (e.color || SOURCE_COLOR[e.source]) + "20", color: e.color || SOURCE_COLOR[e.source] }}>
                        {e.title}
                      </div>
                    ))}
                    {evs.length > 3 && <div className="text-[10px] text-slate-500 px-1.5">+{evs.length - 3} daha</div>}
                  </div>
                </button>
              );
            }))}
          </div>
          </>
          )}

          {view === "week" && (
            <WeekView cells={weekCells} byDate={byDate} HOURS={HOURS}
              todayIso={todayISO()} selectedDate={selectedDate}
              onSelectDay={setSelectedDate} onDblSlot={openNew}
              onMove={moveEvent}
              onEventClick={openEdit}
              SOURCE_COLOR={SOURCE_COLOR} />
          )}
        </div>

        {/* Right column: Selected day + upcoming */}
        <div className="space-y-5">
          <div className="p-5 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Seçili Gün</div>
                <div className="font-display font-extrabold text-lg text-slate-900 dark:text-slate-100">{formatDate(selectedDate)}</div>
              </div>
              <button data-testid="add-event-day" onClick={() => openNew(selectedDate)} className="p-2 rounded-lg bg-[#1E2A9C] text-white hover:bg-[#121A68]"><Plus className="w-4 h-4" /></button>
            </div>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {dayEvents.length === 0 && <div className="text-sm text-slate-400 text-center py-6">Bu gün için etkinlik yok</div>}
              {dayEvents.map(e => (
                <EventCard key={e.id} ev={e} onEdit={openEdit} onDel={del} onToggle={toggle} />
              ))}
            </div>
          </div>

          <div className="p-5 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Yaklaşan</div>
                <div className="font-display font-extrabold text-lg text-slate-900 dark:text-slate-100">Sonraki 10 Etkinlik</div>
              </div>
              <CalendarDays className="w-5 h-5 text-slate-400" />
            </div>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {upcomingEvents.length === 0 && <div className="text-sm text-slate-400 text-center py-6">Yaklaşan etkinlik yok</div>}
              {upcomingEvents.map(e => (
                <div key={e.id} className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: e.color || SOURCE_COLOR[e.source] }} />
                  <div className="font-mono-num text-slate-500 font-bold w-20 flex-shrink-0">{e.date.slice(5)}</div>
                  <div className="flex-1 truncate text-slate-700 dark:text-slate-300 font-semibold">{e.title}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 grid place-items-center p-4">
          <form onSubmit={save} data-testid="event-modal" className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-lg p-8 relative">
            <button type="button" onClick={() => setModal(null)} className="absolute top-4 right-4"><X className="w-5 h-5" /></button>
            <div className="font-display text-2xl font-extrabold mb-6 text-slate-900 dark:text-slate-100">
              {modal.mode === "new" ? "Yeni Etkinlik" : "Etkinliği Düzenle"}
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Başlık</label>
                <input required data-testid="ev-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg dark:bg-slate-800 dark:text-slate-100" />
              </div>
              <div>
                <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Açıklama</label>
                <textarea data-testid="ev-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows="2" className="w-full mt-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg dark:bg-slate-800 dark:text-slate-100"></textarea>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Tarih</label>
                  <input required type="date" data-testid="ev-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg dark:bg-slate-800 dark:text-slate-100" />
                </div>
                <div>
                  <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Saat (Opsiyonel)</label>
                  <input type="time" data-testid="ev-time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg dark:bg-slate-800 dark:text-slate-100" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Tür</label>
                  <select data-testid="ev-type" value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg dark:bg-slate-800 dark:text-slate-100">
                    {Object.entries(TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Öncelik</label>
                  <select data-testid="ev-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg dark:bg-slate-800 dark:text-slate-100">
                    <option value="low">Düşük</option>
                    <option value="medium">Orta</option>
                    <option value="high">Yüksek</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Cari Hesap (Opsiyonel)</label>
                <select data-testid="ev-cari" value={form.cari_id || ""} onChange={(e) => setForm({ ...form, cari_id: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg dark:bg-slate-800 dark:text-slate-100">
                  <option value="">— Yok —</option>
                  {caris.map(c => <option key={c.id} value={c.id}>{c.name} · {c.type === "customer" ? "Müşteri" : "Tedarikçi"}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase font-bold text-slate-500 tracking-widest flex items-center gap-1">
                  <Palette className="w-3.5 h-3.5" /> Renk
                </label>
                <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid="ev-color-picker">
                  {COLOR_PRESETS.map(c => (
                    <button key={c.hex} type="button"
                      data-testid={`color-${c.hex || "auto"}`}
                      onClick={() => setForm({ ...form, color: c.hex })}
                      title={c.label}
                      className={`w-8 h-8 rounded-full grid place-items-center transition ${form.color === c.hex ? "ring-2 ring-offset-2 ring-slate-900 dark:ring-white dark:ring-offset-slate-900" : "hover:scale-110"}`}
                      style={{ backgroundColor: c.hex || "transparent", border: c.hex ? "none" : "2px dashed #94A3B8" }}>
                      {form.color === c.hex && c.hex && <Check className="w-3.5 h-3.5 text-white" />}
                      {!c.hex && <span className="text-[9px] font-bold text-slate-500">AUTO</span>}
                    </button>
                  ))}
                  <div className="relative">
                    <input type="color" data-testid="ev-color-custom" value={form.color || "#1E2A9C"}
                      onChange={(e) => setForm({ ...form, color: e.target.value })}
                      className="w-8 h-8 rounded-full cursor-pointer border-0 opacity-0 absolute inset-0" />
                    <div className="w-8 h-8 rounded-full grid place-items-center bg-gradient-to-tr from-rose-500 via-amber-400 to-emerald-500 text-white pointer-events-none">
                      <Palette className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </div>
              </div>
              <NotesEditor value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} />
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300 cursor-pointer">
                <input type="checkbox" data-testid="ev-remind" checked={form.remind_email}
                  onChange={(e) => setForm({ ...form, remind_email: e.target.checked })} />
                Günlük e-posta özetinde bu etkinlik vurgulansın
              </label>

              {modal.mode === "edit" && (
                <AttachmentsSection ev={modal.ev}
                  onUpload={async (file) => { const a = await uploadAttachment(modal.ev.id, file); setModal({ ...modal, ev: { ...modal.ev, attachments: [...(modal.ev.attachments || []), a] } }); load(); }}
                  onDelete={async (aid) => { await deleteAttachment(modal.ev.id, aid); setModal({ ...modal, ev: { ...modal.ev, attachments: (modal.ev.attachments || []).filter(a => a.id !== aid) } }); load(); }} />
              )}
              {modal.mode === "new" && (
                <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800 text-xs text-slate-500">
                  📎 Etkinliği kaydettikten sonra dosya (PDF/görsel) ekleyebilirsiniz.
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              {modal.mode === "edit" && (
                <button type="button" data-testid="ev-delete" onClick={() => { del(modal.ev); setModal(null); }}
                  className="px-4 py-3 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 font-bold">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <button data-testid="ev-save" className="flex-1 py-3 bg-[#1E2A9C] text-white rounded-lg font-bold hover:bg-[#121A68]">
                {modal.mode === "new" ? "Kaydet" : "Güncelle"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Google Calendar / iCal modal */}
      {icsOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 grid place-items-center p-4">
          <div data-testid="ics-modal" className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-2xl p-8 relative">
            <button onClick={() => setIcsOpen(false)} className="absolute top-4 right-4"><X className="w-5 h-5" /></button>
            <div className="flex items-start gap-3 mb-5">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 grid place-items-center text-white">
                <Link2 className="w-6 h-6" />
              </div>
              <div>
                <div className="font-display text-2xl font-extrabold text-slate-900 dark:text-slate-100">Google Calendar'a Ekle</div>
                <div className="text-sm text-slate-500">Tüm ajanda etkinlikleriniz telefonunuzda otomatik görünsün</div>
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 mb-5">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Takvim URL'i</div>
              <div className="flex gap-2">
                <input data-testid="ics-url" readOnly value={icsUrl}
                  className="flex-1 px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-xs font-mono text-slate-700 dark:text-slate-200" />
                <button data-testid="ics-copy" onClick={copyIcs}
                  className="px-4 py-2 rounded-lg bg-[#1E2A9C] text-white text-xs font-bold hover:bg-[#121A68] flex items-center gap-1">
                  <Copy className="w-3.5 h-3.5" /> Kopyala
                </button>
                <a data-testid="ics-open" href={icsUrl} target="_blank" rel="noreferrer"
                  className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-xs font-bold hover:border-emerald-500 hover:text-emerald-600 flex items-center gap-1">
                  <ExternalLink className="w-3.5 h-3.5" /> İndir
                </a>
              </div>
            </div>

            <div className="space-y-3">
              <Step n="1" title="Google Calendar'ı açın">
                <a href="https://calendar.google.com/calendar/u/0/r/settings/addbyurl" target="_blank" rel="noreferrer"
                  className="text-[#1E2A9C] dark:text-blue-400 underline font-semibold">calendar.google.com &rarr; URL'den ekle</a>
              </Step>
              <Step n="2" title="URL alanına yukarıdaki linki yapıştırın">
                <span className="text-slate-500">Sol menüden "Diğer takvimler" &rarr; + &rarr; "URL'den ekle"</span>
              </Step>
              <Step n="3" title="Telefonunuzda otomatik görünecek">
                <span className="text-slate-500">Google Calendar hesabınıza bağlı tüm cihazlarda etkinlikler ve 30 dk önce hatırlatma alarmı çalışır. Google 6-12 saatte bir günceller.</span>
              </Step>
              <Step n="🍎" title="Apple / iPhone (Alternatif)">
                <span className="text-slate-500">iPhone'da: Ayarlar &rarr; Takvim &rarr; Hesaplar &rarr; Hesap Ekle &rarr; Diğer &rarr; Abone Olunan Takvim Ekle</span>
              </Step>
            </div>

            <div className="mt-5 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300">
              <b>Güvenlik:</b> Bu URL kişiseldir ve etkinliklerinizi içerir. Kimseyle paylaşmayın. Sızarsa şifrenizi değiştirerek yeni bir URL üretebilirsiniz.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#1E2A9C]/10 dark:bg-[#1E2A9C]/30 grid place-items-center text-[#1E2A9C] dark:text-blue-300 text-sm font-extrabold">{n}</div>
      <div className="flex-1 pt-1">
        <div className="text-sm font-bold text-slate-900 dark:text-slate-100">{title}</div>
        <div className="text-xs mt-0.5">{children}</div>
      </div>
    </div>
  );
}

function WeekView({ cells, byDate, HOURS, todayIso, selectedDate, onSelectDay, onDblSlot, onMove, onEventClick, SOURCE_COLOR }) {
  // Compute all-day events (no time) and timed events per day
  const dayBucket = cells.map(d => {
    const dISO = iso(d);
    const evs = byDate[dISO] || [];
    return {
      d, dISO,
      allDay: evs.filter(e => !e.time),
      timed: evs.filter(e => !!e.time),
    };
  });

  const DOW_FULL = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
  const HOUR_HEIGHT = 44;

  const onDragStart = (e, ev) => {
    if (ev.source !== "manual") { e.preventDefault(); return; }
    e.dataTransfer.setData("text/plain", ev.id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDropTimed = (e, dISO, hour) => {
    e.preventDefault();
    const evId = e.dataTransfer.getData("text/plain");
    if (!evId) return;
    // Determine drop position within the hour cell → 15-min snap
    const rect = e.currentTarget.getBoundingClientRect();
    const offY = Math.max(0, Math.min(HOUR_HEIGHT, e.clientY - rect.top));
    const minute = Math.round((offY / HOUR_HEIGHT) * 60 / 15) * 15;
    const mm = Math.min(45, Math.max(0, minute));
    const time = `${String(hour).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
    onMove(evId, dISO, time);
  };
  const onDropAllDay = (e, dISO) => {
    e.preventDefault();
    const evId = e.dataTransfer.getData("text/plain");
    if (evId) onMove(evId, dISO, "");
  };
  const allow = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  return (
    <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden" data-testid="week-view">
      {/* Day headers */}
      <div className="grid" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
        <div className="p-2 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700" />
        {dayBucket.map(({ d, dISO }) => {
          const isToday = dISO === todayIso;
          const isSelected = dISO === selectedDate;
          return (
            <button key={dISO} onClick={() => onSelectDay(dISO)}
              className={`p-2 border-b border-l border-slate-200 dark:border-slate-700 text-center transition ${
                isSelected ? "bg-[#1E2A9C]/10" : isToday ? "bg-emerald-50 dark:bg-emerald-950/30" : "bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700"
              }`}>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{DOW_FULL[(d.getDay() + 6) % 7]}</div>
              <div className={`text-lg font-extrabold ${isToday ? "text-emerald-600" : "text-slate-900 dark:text-slate-100"}`}>{d.getDate()}</div>
            </button>
          );
        })}
      </div>

      {/* All-day strip */}
      <div className="grid" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
        <div className="p-1 text-[9px] uppercase font-bold text-slate-400 tracking-widest border-b border-r border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 grid place-items-center">Tüm Gün</div>
        {dayBucket.map(({ dISO, allDay }) => (
          <div key={dISO} onDragOver={allow} onDrop={(e) => onDropAllDay(e, dISO)}
            className="min-h-[36px] p-1 border-b border-l border-slate-200 dark:border-slate-700 space-y-0.5 transition hover:bg-emerald-50/40 dark:hover:bg-emerald-950/20">
            {allDay.map(e => (
              <div key={e.id}
                data-testid={`wk-ad-${e.id}`}
                draggable={e.source === "manual"}
                onDragStart={(ev) => onDragStart(ev, e)}
                onClick={() => onEventClick && onEventClick(e)}
                className={`text-[10px] px-1.5 py-0.5 rounded truncate font-semibold flex items-center gap-1 ${e.done ? "line-through opacity-50" : ""} ${e.source === "manual" ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"}`}
                style={{ backgroundColor: (e.color || SOURCE_COLOR[e.source]) + "20", color: e.color || SOURCE_COLOR[e.source] }}
                title={e.title}>
                {e.source === "manual" && <GripVertical className="w-2.5 h-2.5 flex-shrink-0 opacity-60" />}
                <span className="truncate">{e.title}</span>
                {(e.attachments || []).length > 0 && <Paperclip className="w-2.5 h-2.5 opacity-70 flex-shrink-0" />}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Hour grid */}
      <div className="relative grid" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
        {/* Hours column */}
        <div className="border-r border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
          {HOURS.map(h => (
            <div key={h} className="text-[10px] text-slate-500 font-mono-num text-right pr-1 border-b border-slate-100 dark:border-slate-700/50" style={{ height: HOUR_HEIGHT }}>
              {String(h).padStart(2, "0")}:00
            </div>
          ))}
        </div>

        {/* Day columns */}
        {dayBucket.map(({ dISO, timed }) => (
          <div key={dISO} className="relative border-l border-slate-200 dark:border-slate-700" style={{ height: HOURS.length * HOUR_HEIGHT }}>
            {HOURS.map((h, i) => (
              <div key={h}
                onDoubleClick={() => onDblSlot(dISO, `${String(h).padStart(2, "0")}:00`)}
                onDragOver={allow}
                onDrop={(e) => onDropTimed(e, dISO, h)}
                className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer transition"
                style={{ height: HOUR_HEIGHT }} />
            ))}
            {timed.map(e => {
              const [hh, mm] = (e.time || "00:00").split(":").map(Number);
              const start = hh + (mm || 0) / 60;
              const top = Math.max(0, (start - HOURS[0]) * HOUR_HEIGHT);
              const height = HOUR_HEIGHT * 0.95;
              const color = e.color || SOURCE_COLOR[e.source] || "#1E2A9C";
              return (
                <div key={e.id}
                  data-testid={`wk-ev-${e.id}`}
                  draggable={e.source === "manual"}
                  onDragStart={(ev) => onDragStart(ev, e)}
                  onClick={() => onEventClick && onEventClick(e)}
                  className={`absolute left-1 right-1 rounded p-1 text-[10px] font-semibold overflow-hidden ${e.done ? "line-through opacity-60" : ""} ${e.source === "manual" ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"}`}
                  style={{ top, height, backgroundColor: color + "25", color, borderLeft: `3px solid ${color}` }}
                  title={e.title}>
                  <div className="truncate flex items-center gap-1">
                    {e.source === "manual" && <GripVertical className="w-2.5 h-2.5 opacity-60 flex-shrink-0" />}
                    <span className="truncate">{e.time} {e.title}</span>
                    {(e.attachments || []).length > 0 && <Paperclip className="w-2.5 h-2.5 opacity-70 flex-shrink-0" />}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function EventCard({ ev, onEdit, onDel, onToggle }) {
  const color = ev.color || SOURCE_COLOR[ev.source] || TYPE_COLOR[ev.event_type] || "#64748B";
  const isManual = ev.source === "manual";
  const attCount = (ev.attachments || []).length;
  const hasNotes = (ev.notes || "").trim().length > 0;
  return (
    <div data-testid={`event-${ev.id}`} className={`p-3 rounded-lg border border-slate-200 dark:border-slate-700 group hover:border-slate-400 transition ${ev.done ? "opacity-60" : ""}`}
      style={{ borderLeftWidth: 4, borderLeftColor: color }}>
      <div className="flex items-start gap-2">
        {isManual && (
          <button onClick={() => onToggle(ev)} className="mt-0.5 flex-shrink-0">
            {ev.done ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <div className="w-4 h-4 rounded-full border-2 border-slate-300 hover:border-emerald-500" />}
          </button>
        )}
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-bold text-slate-900 dark:text-slate-100 ${ev.done ? "line-through" : ""}`}>{ev.title}</div>
          {ev.description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{ev.description}</div>}
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded" style={{ color, backgroundColor: color + "15" }}>
              {SOURCE_LABEL[ev.source]}
            </span>
            {ev.time && <span className="text-[10px] text-slate-500 font-mono-num flex items-center gap-1"><Clock className="w-3 h-3" />{ev.time}</span>}
            {ev.priority === "high" && <AlertCircle className="w-3 h-3 text-rose-500" />}
            {attCount > 0 && (
              <span className="text-[10px] text-slate-500 font-bold flex items-center gap-1">
                <Paperclip className="w-3 h-3" />{attCount}
              </span>
            )}
            {hasNotes && (
              <span className="text-[10px] text-slate-500 font-bold flex items-center gap-1" title="Toplantı notu var">
                <StickyNote className="w-3 h-3" />
              </span>
            )}
          </div>
        </div>
        {isManual && (
          <button onClick={() => onEdit(ev)} className="opacity-0 group-hover:opacity-100 transition text-slate-400 hover:text-[#1E2A9C] text-xs font-bold">
            Düzenle
          </button>
        )}
      </div>
    </div>
  );
}

function NotesEditor({ value, onChange }) {
  const [mode, setMode] = useState("edit"); // edit | preview
  const insert = (before, after = "") => {
    const ta = document.querySelector('[data-testid="ev-notes"]');
    if (!ta) return;
    const s = ta.selectionStart || 0;
    const e = ta.selectionEnd || 0;
    const sel = value.substring(s, e) || "metin";
    const next = value.substring(0, s) + before + sel + after + value.substring(e);
    onChange(next);
    setTimeout(() => { ta.focus(); ta.setSelectionRange(s + before.length, s + before.length + sel.length); }, 0);
  };
  return (
    <div className="space-y-2" data-testid="notes-editor">
      <div className="flex items-center justify-between">
        <label className="text-xs uppercase font-bold text-slate-500 tracking-widest flex items-center gap-1">
          <StickyNote className="w-3.5 h-3.5" /> Toplantı Notu (Markdown)
        </label>
        <div className="flex items-center gap-1 p-0.5 bg-slate-100 dark:bg-slate-800 rounded">
          <button type="button" data-testid="notes-edit" onClick={() => setMode("edit")}
            className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold transition ${mode === "edit" ? "bg-white dark:bg-slate-900 text-[#1E2A9C] shadow-sm" : "text-slate-500"}`}>
            <Pencil className="w-3 h-3" /> Yaz
          </button>
          <button type="button" data-testid="notes-preview" onClick={() => setMode("preview")}
            className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold transition ${mode === "preview" ? "bg-white dark:bg-slate-900 text-[#1E2A9C] shadow-sm" : "text-slate-500"}`}>
            <Eye className="w-3 h-3" /> Önizle
          </button>
        </div>
      </div>
      {mode === "edit" && (
        <>
          <div className="flex items-center gap-1 p-1 bg-slate-50 dark:bg-slate-800 rounded-lg">
            {[
              { l: "B", t: "Kalın", b: "**", a: "**" },
              { l: "I", t: "Eğik", b: "*", a: "*" },
              { l: "H", t: "Başlık", b: "## ", a: "" },
              { l: "•", t: "Liste", b: "- ", a: "" },
              { l: "☑", t: "Görev", b: "- [ ] ", a: "" },
              { l: "🔗", t: "Bağlantı", b: "[", a: "](url)" },
              { l: "❝", t: "Alıntı", b: "> ", a: "" },
              { l: "</>", t: "Kod", b: "`", a: "`" },
            ].map(t => (
              <button key={t.l} type="button" title={t.t} onClick={() => insert(t.b, t.a)}
                className="px-2 py-0.5 rounded text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700 hover:text-[#1E2A9C]">
                {t.l}
              </button>
            ))}
          </div>
          <textarea data-testid="ev-notes" rows={6}
            value={value} onChange={(e) => onChange(e.target.value)}
            placeholder={"Toplantı gündemi, kararlar, aksiyon maddeleri...\n\n## Örnek\n- Fiyat teklifi gönderilecek\n- [ ] Sözleşme taslağı hazırla"}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg dark:bg-slate-800 dark:text-slate-100 font-mono text-sm resize-y" />
        </>
      )}
      {mode === "preview" && (
        <div data-testid="notes-preview-panel" className="min-h-[160px] p-4 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 max-w-none text-sm">
          {value ? (
            <ReactMarkdown components={{
              h1: (p) => <h1 className="text-xl font-extrabold mt-2 mb-1 text-slate-900 dark:text-slate-100" {...p} />,
              h2: (p) => <h2 className="text-lg font-extrabold mt-2 mb-1 text-slate-900 dark:text-slate-100" {...p} />,
              h3: (p) => <h3 className="text-base font-bold mt-2 mb-1 text-slate-900 dark:text-slate-100" {...p} />,
              p: (p) => <p className="my-1 text-slate-700 dark:text-slate-300 leading-relaxed" {...p} />,
              ul: (p) => <ul className="list-disc pl-5 my-1 space-y-0.5 text-slate-700 dark:text-slate-300" {...p} />,
              ol: (p) => <ol className="list-decimal pl-5 my-1 space-y-0.5 text-slate-700 dark:text-slate-300" {...p} />,
              li: (p) => <li className="text-slate-700 dark:text-slate-300" {...p} />,
              blockquote: (p) => <blockquote className="border-l-4 border-[#1E2A9C] pl-3 my-2 italic text-slate-600 dark:text-slate-400" {...p} />,
              code: ({inline, ...p}) => inline
                ? <code className="px-1 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-rose-600 dark:text-rose-400 text-xs font-mono" {...p} />
                : <code className="block p-3 rounded bg-slate-900 dark:bg-black text-emerald-300 text-xs font-mono overflow-x-auto" {...p} />,
              a: (p) => <a target="_blank" rel="noreferrer" className="text-[#1E2A9C] dark:text-blue-400 underline" {...p} />,
              strong: (p) => <strong className="font-bold text-slate-900 dark:text-slate-100" {...p} />,
              em: (p) => <em className="italic" {...p} />,
              hr: () => <hr className="my-3 border-slate-300 dark:border-slate-600" />,
            }}>{value}</ReactMarkdown>
          ) : <div className="text-slate-400 italic text-sm">Not yok</div>}
        </div>
      )}
    </div>
  );
}

function AttachmentsSection({ ev, onUpload, onDelete }) {
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const attachments = ev.attachments || [];

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        // 10 MB limit
        if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} çok büyük (max 10MB)`); continue; }
        await onUpload(f);
      }
    } finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    handleFiles(Array.from(e.dataTransfer.files || []));
  };

  const fmt = (n) => n < 1024 ? `${n} B` : n < 1024*1024 ? `${(n/1024).toFixed(1)} KB` : `${(n/1024/1024).toFixed(1)} MB`;
  const isImg = (ct) => (ct || "").startsWith("image/");
  const isPdf = (ct) => (ct || "").includes("pdf");

  return (
    <div data-testid="attachments-section" className="space-y-2">
      <label className="text-xs uppercase font-bold text-slate-500 tracking-widest flex items-center gap-1">
        <Paperclip className="w-3.5 h-3.5" /> Ekler ({attachments.length})
      </label>
      <label
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        className={`flex flex-col items-center justify-center gap-1 p-4 rounded-lg border-2 border-dashed cursor-pointer transition ${
          drag ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20" : "border-slate-300 dark:border-slate-600 hover:border-[#1E2A9C]"
        }`}>
        <input data-testid="att-file" type="file" className="hidden" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
          onChange={(e) => { handleFiles(Array.from(e.target.files || [])); e.target.value = ""; }} />
        <Upload className="w-5 h-5 text-slate-400" />
        <div className="text-xs font-bold text-slate-600 dark:text-slate-300">{uploading ? "Yükleniyor..." : "PDF veya görsel yüklemek için tıkla / sürükle"}</div>
        <div className="text-[10px] text-slate-400">Max 10MB · PDF, JPG, PNG, DOCX, XLSX</div>
      </label>

      {attachments.length > 0 && (
        <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
          {attachments.map(a => (
            <div key={a.id} data-testid={`att-${a.id}`} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 dark:bg-slate-800 group">
              {isImg(a.content_type) ? (
                <a href={API_BASE + a.url} target="_blank" rel="noreferrer" className="w-10 h-10 rounded overflow-hidden flex-shrink-0 bg-slate-200">
                  <img src={API_BASE + a.url} alt={a.name} className="w-full h-full object-cover" />
                </a>
              ) : (
                <div className={`w-10 h-10 rounded flex-shrink-0 grid place-items-center ${isPdf(a.content_type) ? "bg-rose-100 text-rose-600" : "bg-slate-200 dark:bg-slate-700 text-slate-500"}`}>
                  {isPdf(a.content_type) ? <FileIcon className="w-5 h-5" /> : <FileIcon className="w-5 h-5" />}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">{a.name}</div>
                <div className="text-[10px] text-slate-500 font-mono-num">{fmt(a.size)} · {new Date(a.uploaded_at).toLocaleDateString("tr-TR")}</div>
              </div>
              <a href={API_BASE + a.url} target="_blank" rel="noreferrer"
                className="p-1.5 rounded text-slate-500 hover:text-[#1E2A9C] hover:bg-white dark:hover:bg-slate-700">
                <Download className="w-3.5 h-3.5" />
              </a>
              <button type="button" onClick={() => onDelete(a.id)}
                className="p-1.5 rounded text-slate-500 hover:text-rose-600 hover:bg-white dark:hover:bg-slate-700 opacity-0 group-hover:opacity-100 transition">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
